/**
 * Utility functions for calculating learning streaks with proper 24-hour gap validation
 */

/**
 * Calculate the current learning streak based on user progress data
 * A streak requires consecutive days with at least 24 hours between completions
 * 
 * @param userProgress - Array of user progress records with completion dates
 * @returns The current learning streak in days
 */
export const calculateLearningStreak = (userProgress: any[]): number => {
  // Filter only completed lessons and extract completion dates
  const completedLessons = userProgress
    .filter(progress => progress.status === 'completed' && progress.completed_at)
    .map(progress => new Date(progress.completed_at))
    .sort((a, b) => b.getTime() - a.getTime()); // Sort by most recent first

  if (completedLessons.length === 0) {
    return 0;
  }

  console.log("📅 Calculating streak from completed lessons:", completedLessons.length);

  // Get unique days (ignore time, just dates) but keep the earliest completion time for each day
  const dailyCompletions = new Map();
  
  completedLessons.forEach(date => {
    const dayKey = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    
    // Keep the earliest completion time for each day
    if (!dailyCompletions.has(dayKey) || date.getTime() < dailyCompletions.get(dayKey).getTime()) {
      dailyCompletions.set(dayKey, date);
    }
  });

  // Convert back to array and sort by most recent first
  const uniqueDayCompletions = Array.from(dailyCompletions.values())
    .sort((a, b) => b.getTime() - a.getTime());

  console.log("📅 Unique completion days with times:", uniqueDayCompletions.map(d => ({
    date: d.toDateString(),
    time: d.toTimeString()
  })));

  let streak = 0;
  const now = new Date();
  
  // Check if the most recent completion was within the last 48 hours
  // (allowing for some flexibility for the current day)
  const mostRecentCompletion = uniqueDayCompletions[0];
  const hoursSinceLastCompletion = (now.getTime() - mostRecentCompletion.getTime()) / (1000 * 60 * 60);
  
  if (hoursSinceLastCompletion > 48) {
    // Streak is broken if no activity in the last 48 hours
    console.log("📅 Streak broken - no recent activity within 48 hours");
    return 0;
  }

  // Count consecutive days with proper 24-hour gaps
  for (let i = 0; i < uniqueDayCompletions.length - 1; i++) {
    const currentCompletion = uniqueDayCompletions[i];
    const nextCompletion = uniqueDayCompletions[i + 1];
    
    // Calculate hours between completions
    const hoursBetween = (currentCompletion.getTime() - nextCompletion.getTime()) / (1000 * 60 * 60);
    
    console.log(`📅 Hours between completion ${i} and ${i + 1}:`, hoursBetween);
    
    // For the first day, always count it
    if (i === 0) {
      streak = 1;
    }
    
    // Check if there's at least 20 hours between completions (allowing some flexibility)
    // and not more than 48 hours (to ensure it's consecutive days)
    if (hoursBetween >= 20 && hoursBetween <= 48) {
      streak++;
    } else {
      // Gap is too small (same day) or too large (skipped days), break streak
      console.log(`📅 Breaking streak: gap of ${hoursBetween} hours is not valid`);
      break;
    }
  }

  // If we only have one completion, the streak is 1
  if (uniqueDayCompletions.length === 1) {
    streak = 1;
  }

  console.log("📅 Calculated learning streak with 24-hour validation:", streak, "days");
  return streak;
};

/**
 * Get the last completion date for display purposes
 * 
 * @param userProgress - Array of user progress records
 * @returns The most recent completion date or null
 */
export const getLastCompletionDate = (userProgress: any[]): Date | null => {
  const completedLessons = userProgress
    .filter(progress => progress.status === 'completed' && progress.completed_at)
    .map(progress => new Date(progress.completed_at))
    .sort((a, b) => b.getTime() - a.getTime());

  return completedLessons.length > 0 ? completedLessons[0] : null;
};
